import { Route, Navigate, useParams } from 'react-router-dom';

/**
 * Panel route tree mirrored from apps/panel/src/App.tsx so the web shell
 * can serve every /panel/* link the sidebar advertises. Keep this file in
 * sync with apps/panel/src/App.tsx.
 *
 * Cleaned 2026-05-07 — e-commerce template (shop, cart, checkout, account,
 * banner, store, attribute, company, tenant-domain-map, order-return,
 * feature, public-catalog) removed after live API audit.
 */

import Dashboard from '../../panel/src/pages/Dashboard';
import MainDashboard from '../../panel/src/pages/MainDashboard';
import ComingSoon from '../../panel/src/pages/ComingSoon';
import UserProfile from '../../panel/src/pages/profile/UserProfile';
import ApiBrowser from '../../panel/src/pages/api-browser/ApiBrowser';
import AnalyticsDashboard from '../../panel/src/pages/api-browser/AnalyticsDashboard';
import AnalyticsLive from '../../panel/src/pages/analytics/AnalyticsLive';
import SalesInvoiceList from '../../panel/src/pages/sales/SalesInvoiceList';
import SalesInvoiceComposer from '../../panel/src/pages/sales/SalesInvoiceComposer';
import PurchaseOrderList from '../../panel/src/pages/purchase/PurchaseOrderList';
import PurchaseOrderComposer from '../../panel/src/pages/purchase/PurchaseOrderComposer';
import PosBilling from '../../panel/src/pages/pos/PosBilling';
import RestaurantFloor from '../../panel/src/pages/restaurant/RestaurantFloor';
import RestaurantOrders from '../../panel/src/pages/restaurant/RestaurantOrders';
import KotBoard from '../../panel/src/pages/restaurant/KotBoard';
import TableReservations from '../../panel/src/pages/restaurant/TableReservations';
import PartyList from '../../panel/src/pages/parties/PartyList';
import PartyForm from '../../panel/src/pages/parties/PartyForm';
import KitchenDisplay from '../../panel/src/pages/kitchen/KitchenDisplay';
import TableStatusBoard from '../../panel/src/pages/kitchen/TableStatusBoard';
import TableQrCodes from '../../panel/src/pages/kitchen/TableQrCodes';
import WaiterQueue from '../../panel/src/pages/kitchen/WaiterQueue';
import InventoryDashboard from '../../panel/src/pages/inventory/InventoryDashboard';
import StockTakeWizard from '../../panel/src/pages/inventory/StockTakeWizard';
import JournalEntryList from '../../panel/src/pages/accounting/JournalEntryList';
import JournalVoucherComposer from '../../panel/src/pages/accounting/JournalVoucherComposer';
import PayrollRunList from '../../panel/src/pages/payroll/PayrollRunList';
import PayrollRunWizard from '../../panel/src/pages/payroll/PayrollRunWizard';
import CrmPipeline from '../../panel/src/pages/crm/CrmPipeline';
import ProductionGantt from '../../panel/src/pages/manufacturing/ProductionGantt';
import ReportDefinitionList from '../../panel/src/pages/reports/ReportDefinitionList';
import ReportBuilder from '../../panel/src/pages/reports/ReportBuilder';
import PartyWiseReport from '../../panel/src/pages/reports/PartyWiseReport';
import MfaPage from '../../panel/src/pages/security/MfaPage';
import ApiKeysPage from '../../panel/src/pages/security/ApiKeysPage';
import MyPermissionsPage from '../../panel/src/pages/security/MyPermissionsPage';
import GrantAdminPage from '../../panel/src/pages/security/GrantAdminPage';
import UserRolesPage from '../../panel/src/pages/security/UserRolesPage';

import AllRecruitments from '../../panel/src/pages/recruitment/AllRecruitments';
import NewRecruitment from '../../panel/src/pages/recruitment/NewRecruitment';
import AllEssRequests from '../../panel/src/pages/ess/AllEssRequests';
import NewEssRequest from '../../panel/src/pages/ess/NewEssRequest';
import AllLeaves from '../../panel/src/pages/leave/AllLeaves';
import NewLeave from '../../panel/src/pages/leave/NewLeave';
import AllOnboardings from '../../panel/src/pages/onboarding/AllOnboardings';
import NewOnboarding from '../../panel/src/pages/onboarding/NewOnboarding';
import AllReviews from '../../panel/src/pages/performance/AllReviews';
import NewReview from '../../panel/src/pages/performance/NewReview';
import AllShifts from '../../panel/src/pages/time-shift/AllShifts';
import NewShift from '../../panel/src/pages/time-shift/NewShift';
import AllTravels from '../../panel/src/pages/travel/AllTravels';
import NewTravel from '../../panel/src/pages/travel/NewTravel';
import AllTrainings from '../../panel/src/pages/training/AllTrainings';
import NewTraining from '../../panel/src/pages/training/NewTraining';
import AllExits from '../../panel/src/pages/exit/AllExits';
import NewExit from '../../panel/src/pages/exit/NewExit';

import AllWorkflows from '../../panel/src/pages/workflow/AllWorkflows';
import NewWorkflow from '../../panel/src/pages/workflow/NewWorkflow';
import ApprovalInbox from '../../panel/src/pages/workflow/ApprovalInbox';
import AuditLog from '../../panel/src/pages/audit/AuditLog';

import AllUsers from '../../panel/src/pages/user/AllUsers';
import NewUser from '../../panel/src/pages/user/NewUser';
import AllRoles from '../../panel/src/pages/role/AllRoles';
import NewRole from '../../panel/src/pages/role/NewRole';
import AllMenus from '../../panel/src/pages/menu/AllMenus';
import NewMenu from '../../panel/src/pages/menu/NewMenu';
import AllTenants from '../../panel/src/pages/tenant/AllTenants';
import NewTenant from '../../panel/src/pages/tenant/NewTenant';
import AllSubscriptionPlans from '../../panel/src/pages/subscription-plan/AllSubscriptionPlans';
import NewSubscriptionPlan from '../../panel/src/pages/subscription-plan/NewSubscriptionPlan';

import AllBrands from '../../panel/src/pages/brand/AllBrands';
import NewBrand from '../../panel/src/pages/brand/NewBrand';
import AllUnits from '../../panel/src/pages/unit/AllUnits';
import NewUnit from '../../panel/src/pages/unit/NewUnit';

import AllCategories from '../../panel/src/pages/category/AllCategories';
import NewCategory from '../../panel/src/pages/category/NewCategory';
import AllProducts from '../../panel/src/pages/product/AllProducts';
import NewProduct from '../../panel/src/pages/product/NewProduct';

import AllCustomers from '../../panel/src/pages/customer/AllCustomers';
import NewCustomer from '../../panel/src/pages/customer/NewCustomer';
import AllSuppliers from '../../panel/src/pages/supplier/AllSuppliers';
import NewSupplier from '../../panel/src/pages/supplier/NewSupplier';

// Extended master / sales / restaurant (2026-05-08).
import AllBranches from '../../panel/src/pages/branch/AllBranches';
import NewBranch from '../../panel/src/pages/branch/NewBranch';
import AllDepartments from '../../panel/src/pages/department/AllDepartments';
import NewDepartment from '../../panel/src/pages/department/NewDepartment';
import AllDesignations from '../../panel/src/pages/designation/AllDesignations';
import NewDesignation from '../../panel/src/pages/designation/NewDesignation';
import AllBanks from '../../panel/src/pages/bank/AllBanks';
import NewBank from '../../panel/src/pages/bank/NewBank';
import AllCoupons from '../../panel/src/pages/coupon/AllCoupons';
import NewCoupon from '../../panel/src/pages/coupon/NewCoupon';
import AllPromotions from '../../panel/src/pages/promotion/AllPromotions';
import NewPromotion from '../../panel/src/pages/promotion/NewPromotion';
import AllSalesReturns from '../../panel/src/pages/sales-return/AllSalesReturns';
import AllRestaurantTables from '../../panel/src/pages/restaurant-table/AllRestaurantTables';
import NewRestaurantTable from '../../panel/src/pages/restaurant-table/NewRestaurantTable';

// Newly added pages mirrored from apps/panel/src/App.tsx so the web
// shell's /panel/* routing matches. These are what the "+ New" buttons
// on every grid navigate to.
import TransactionEntryShell from '../../panel/src/pages/transactions/TransactionEntryShell';
import InvoiceIngest from '../../panel/src/pages/transactions/InvoiceIngest';
import GenericFullPageForm from '../../panel/src/pages/api-browser/GenericFullPageForm';
import QuotationComposer from '../../panel/src/pages/crm/QuotationComposer';
import CreditDebitNoteComposer from '../../panel/src/pages/sales/CreditDebitNoteComposer';
import DeliveryChallanComposer from '../../panel/src/pages/sales/DeliveryChallanComposer';
import SalesPaymentComposer from '../../panel/src/pages/sales/SalesPaymentComposer';
import ModuleRedirect from '../../panel/src/routes/ModuleRedirect';
import PrintFormats from '../../panel/src/pages/settings/PrintFormats';

/**
 * Redirect a friendly `/panel/menu-items/:id` (or menu-categories) edit URL
 * to the registry editor `/panel/api/restaurant/<Resource>/:id`, preserving
 * the row id. The registry GenericFullPageForm loads + edits the row
 * correctly and the list (ApiBrowser) brings bulk spreadsheet, Excel
 * import/export/template, row checkboxes — mirrors apps/panel/src/App.tsx.
 */
function MenuRegistryEdit({ resource }: { resource: string }) {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/panel/api/restaurant/${resource}/${id ?? ''}`} replace />;
}

export function renderPanelRoutes() {
  return (
    <>
      <Route index element={<MainDashboard />} />
      <Route path="dashboard" element={<MainDashboard />} />
      <Route path="dashboard/hr" element={<Dashboard />} />
      <Route path="profile" element={<UserProfile />} />

      {/* Unified Party master (Customer + Supplier). */}
      <Route path="parties" element={<PartyList />} />
      <Route path="parties/new" element={<PartyForm />} />

      {/* Invoices SEPARATED per module — Sales / POS / Dine-in each its own page. */}
      <Route path="sales/invoices" element={<SalesInvoiceList source="Self" />} />
      <Route path="sales/invoices/new" element={<SalesInvoiceComposer />} />
      <Route path="sales/invoices/:id" element={<SalesInvoiceComposer />} />

      <Route path="purchase/orders" element={<PurchaseOrderList />} />
      <Route path="purchase/orders/new" element={<PurchaseOrderComposer />} />
      <Route path="purchase/orders/:id" element={<PurchaseOrderComposer />} />

      <Route path="pos/billing" element={<PosBilling />} />
      <Route path="pos/invoices" element={<SalesInvoiceList source="POS" />} />

      <Route path="restaurant/floor" element={<RestaurantFloor />} />
      <Route path="restaurant/invoices" element={<SalesInvoiceList source="Dine-in" />} />
      <Route path="restaurant/kitchen" element={<KitchenDisplay />} />
      <Route path="restaurant/orders" element={<TableStatusBoard />} />
      <Route path="restaurant/qr-codes" element={<TableQrCodes />} />
      <Route path="restaurant/waiter" element={<WaiterQueue />} />
      {/* Legacy /kitchen/* aliases — keep working for old bookmarks */}
      <Route path="kitchen" element={<KitchenDisplay />} />
      <Route path="kitchen/tables" element={<TableStatusBoard />} />
      <Route path="kitchen/qr-codes" element={<TableQrCodes />} />
      <Route path="waiter" element={<WaiterQueue />} />

      <Route path="inventory" element={<InventoryDashboard />} />
      <Route path="inventory/stock-take" element={<StockTakeWizard />} />

      <Route path="accounting/journal" element={<JournalEntryList />} />
      <Route path="accounting/journal/new" element={<JournalVoucherComposer />} />
      <Route path="accounting/journal/:id" element={<JournalVoucherComposer />} />

      <Route path="payroll/runs" element={<PayrollRunList />} />
      <Route path="payroll/runs/new" element={<PayrollRunWizard />} />
      <Route path="payroll/runs/:id" element={<PayrollRunWizard />} />

      <Route path="crm/pipeline" element={<CrmPipeline />} />

      <Route path="manufacturing/gantt" element={<ProductionGantt />} />

      <Route path="reports" element={<ReportDefinitionList />} />
      <Route path="reports/party-wise" element={<PartyWiseReport />} />
      <Route path="reports/new" element={<ReportBuilder />} />
      <Route path="reports/:id" element={<ReportBuilder />} />

      <Route path="profile/mfa" element={<MfaPage />} />
      <Route path="profile/api-keys" element={<ApiKeysPage />} />
      <Route path="profile/permissions" element={<MyPermissionsPage />} />
      <Route path="security/grant-admin" element={<GrantAdminPage />} />
      <Route path="security/user-roles" element={<UserRolesPage />} />
      <Route path="api/identity/User/roles" element={<UserRolesPage />} />
      <Route path="settings/print-formats" element={<PrintFormats />} />

      {/* Restaurant Orders / KOTs / Reservations — readable table-wise card
          boards (override the generic grids that dumped raw JSON/columns). */}
      <Route path="api/restaurant/RestaurantOrder" element={<RestaurantOrders />} />
      <Route path="api/restaurant/Kot" element={<KotBoard />} />
      <Route path="api/restaurant/TableReservation" element={<TableReservations />} />
      <Route path="api" element={<ApiBrowser />} />
      <Route path="api/:group" element={<ApiBrowser />} />
      <Route path="api/:group/analytics" element={<AnalyticsDashboard />} />
      <Route path="api/:group/:resource" element={<ApiBrowser />} />
      <Route path="analytics" element={<AnalyticsLive />} />

      {/* Transaction-entry shells (Business Info + Customer/Vendor + Line */}
      {/* Items + Summary). Flat URLs read like "Panel > Credit Notes > New". */}
      <Route path="sales-orders/new"        element={<TransactionEntryShell kind="SalesOrder" />} />
      <Route path="sales-orders/:id"        element={<TransactionEntryShell kind="SalesOrder" />} />
      <Route path="sales-invoices/new"      element={<TransactionEntryShell kind="SalesInvoice" />} />
      <Route path="sales-invoices/:id"      element={<TransactionEntryShell kind="SalesInvoice" />} />
      {/* sales-returns list path is wired further down to AllSalesReturns;
          add /new + /:id here for the shell.                              */}
      <Route path="credit-notes/new"        element={<CreditDebitNoteComposer kind="credit" />} />
      <Route path="credit-notes/:id"        element={<CreditDebitNoteComposer kind="credit" />} />
      <Route path="debit-notes/new"         element={<CreditDebitNoteComposer kind="debit" />} />
      <Route path="debit-notes/:id"         element={<CreditDebitNoteComposer kind="debit" />} />
      <Route path="delivery-challans/new"   element={<DeliveryChallanComposer />} />
      <Route path="delivery-challans/:id"   element={<DeliveryChallanComposer />} />
      <Route path="purchase-orders/new"     element={<TransactionEntryShell kind="PurchaseOrder" />} />
      <Route path="purchase-orders/:id"     element={<TransactionEntryShell kind="PurchaseOrder" />} />
      <Route path="purchase-invoices/new"   element={<TransactionEntryShell kind="PurchaseInvoice" />} />
      <Route path="purchase-invoices/:id"   element={<TransactionEntryShell kind="PurchaseInvoice" />} />

      {/* Upload a PDF tax-invoice → review → post (§36) */}
      <Route path="transactions/ingest"     element={<InvoiceIngest />} />
      <Route path="invoice-ingest"          element={<InvoiceIngest />} />

      {/* Legacy /panel/api/<group>/<entity>/new still wired for backward */}
      {/* compat with bookmarks created before the flat URLs landed.       */}
      <Route path="api/sales/SalesOrder/new"      element={<TransactionEntryShell kind="SalesOrder" />} />
      <Route path="api/sales/SalesOrder/:id"      element={<TransactionEntryShell kind="SalesOrder" />} />
      <Route path="api/sales/SalesInvoice/new"    element={<TransactionEntryShell kind="SalesInvoice" />} />
      <Route path="api/sales/SalesInvoice/:id"    element={<TransactionEntryShell kind="SalesInvoice" />} />
      <Route path="api/sales/SalesReturn/new"     element={<CreditDebitNoteComposer kind="return" />} />
      <Route path="api/sales/SalesReturn/:id"     element={<CreditDebitNoteComposer kind="return" />} />
      <Route path="api/sales/SalesPayment/new"    element={<SalesPaymentComposer />} />
      <Route path="api/sales/SalesPayment/:id"    element={<SalesPaymentComposer />} />
      <Route path="api/sales/CreditNote/new"      element={<CreditDebitNoteComposer kind="credit" />} />
      <Route path="api/sales/CreditNote/:id"      element={<CreditDebitNoteComposer kind="credit" />} />
      <Route path="api/sales/DebitNote/new"       element={<CreditDebitNoteComposer kind="debit" />} />
      <Route path="api/sales/DebitNote/:id"       element={<CreditDebitNoteComposer kind="debit" />} />
      <Route path="api/sales/DeliveryChallan/new" element={<DeliveryChallanComposer />} />
      <Route path="api/sales/DeliveryChallan/:id" element={<DeliveryChallanComposer />} />
      <Route path="api/purchase/PurchaseOrder/new"   element={<TransactionEntryShell kind="PurchaseOrder" />} />
      <Route path="api/purchase/PurchaseOrder/:id"   element={<TransactionEntryShell kind="PurchaseOrder" />} />
      <Route path="api/purchase/PurchaseInvoice/new" element={<TransactionEntryShell kind="PurchaseInvoice" />} />
      <Route path="api/purchase/PurchaseInvoice/:id" element={<TransactionEntryShell kind="PurchaseInvoice" />} />
      {/* ⑥ Quotation — custom composer with Sales Order dropdown + auto-fill. */}
      <Route path="api/crm/Quotation/new" element={<QuotationComposer />} />
      <Route path="api/crm/Quotation/:id"  element={<QuotationComposer />} />

      {/* Generic full-page form for every other registry resource. */}
      <Route path="api/:group/:resource/new" element={<GenericFullPageForm />} />
      <Route path="api/:group/:resource/:id" element={<GenericFullPageForm />} />

      <Route path="recruitments" element={<AllRecruitments />} />
      <Route path="recruitments/new" element={<NewRecruitment />} />
      <Route path="recruitments/:id" element={<NewRecruitment />} />

      <Route path="ess" element={<AllEssRequests />} />
      <Route path="ess/new" element={<NewEssRequest />} />
      <Route path="ess/:id" element={<NewEssRequest />} />

      <Route path="leaves" element={<AllLeaves />} />
      <Route path="leaves/new" element={<NewLeave />} />
      <Route path="leaves/:id" element={<NewLeave />} />

      <Route path="onboardings" element={<AllOnboardings />} />
      <Route path="onboardings/new" element={<NewOnboarding />} />
      <Route path="onboardings/:id" element={<NewOnboarding />} />

      <Route path="performances" element={<AllReviews />} />
      <Route path="performances/new" element={<NewReview />} />
      <Route path="performances/:id" element={<NewReview />} />

      <Route path="shifts" element={<AllShifts />} />
      <Route path="shifts/new" element={<NewShift />} />
      <Route path="shifts/:id" element={<NewShift />} />

      <Route path="travels" element={<AllTravels />} />
      <Route path="travels/new" element={<NewTravel />} />
      <Route path="travels/:id" element={<NewTravel />} />

      <Route path="trainings" element={<AllTrainings />} />
      <Route path="trainings/new" element={<NewTraining />} />
      <Route path="trainings/:id" element={<NewTraining />} />

      <Route path="exits" element={<AllExits />} />
      <Route path="exits/new" element={<NewExit />} />
      <Route path="exits/:id" element={<NewExit />} />

      <Route path="workflows" element={<AllWorkflows />} />
      <Route path="workflows/new" element={<NewWorkflow />} />
      <Route path="workflows/:id" element={<NewWorkflow />} />
      <Route path="approvals" element={<ApprovalInbox />} />
      <Route path="audit-log" element={<AuditLog />} />

      <Route path="users" element={<AllUsers />} />
      <Route path="users/new" element={<NewUser />} />
      <Route path="users/:id" element={<NewUser />} />
      <Route path="roles" element={<AllRoles />} />
      <Route path="roles/new" element={<NewRole />} />
      <Route path="roles/:id" element={<NewRole />} />
      <Route path="menus" element={<AllMenus />} />
      <Route path="menus/new" element={<NewMenu />} />
      <Route path="menus/:id" element={<NewMenu />} />
      <Route path="tenants" element={<AllTenants />} />
      <Route path="tenants/new" element={<NewTenant />} />
      <Route path="tenants/:id" element={<NewTenant />} />
      <Route path="subscription-plans" element={<AllSubscriptionPlans />} />
      <Route path="subscription-plans/new" element={<NewSubscriptionPlan />} />
      <Route path="subscription-plans/:id" element={<NewSubscriptionPlan />} />

      <Route path="brands" element={<AllBrands />} />
      <Route path="brands/new" element={<NewBrand />} />
      <Route path="brands/:id" element={<NewBrand />} />
      <Route path="units" element={<AllUnits />} />
      <Route path="units/new" element={<NewUnit />} />
      <Route path="units/:id" element={<NewUnit />} />

      <Route path="categories" element={<AllCategories />} />
      <Route path="categories/new" element={<NewCategory />} />
      <Route path="categories/:id" element={<NewCategory />} />

      <Route path="products" element={<AllProducts />} />
      <Route path="products/new" element={<NewProduct />} />
      <Route path="products/:id" element={<NewProduct />} />

      <Route path="customers" element={<AllCustomers />} />
      <Route path="customers/new" element={<NewCustomer />} />
      <Route path="customers/:id" element={<NewCustomer />} />
      <Route path="suppliers" element={<AllSuppliers />} />
      <Route path="suppliers/new" element={<NewSupplier />} />
      <Route path="suppliers/:id" element={<NewSupplier />} />

      {/* Master — branches/departments/designations/banks */}
      <Route path="branches" element={<AllBranches />} />
      <Route path="branches/new" element={<NewBranch />} />
      <Route path="branches/:id" element={<NewBranch />} />
      <Route path="departments" element={<AllDepartments />} />
      <Route path="departments/new" element={<NewDepartment />} />
      <Route path="departments/:id" element={<NewDepartment />} />
      <Route path="designations" element={<AllDesignations />} />
      <Route path="designations/new" element={<NewDesignation />} />
      <Route path="designations/:id" element={<NewDesignation />} />
      <Route path="banks" element={<AllBanks />} />
      <Route path="banks/new" element={<NewBank />} />
      <Route path="banks/:id" element={<NewBank />} />

      {/* Sales — coupons/promotions/sales-returns */}
      <Route path="coupons" element={<AllCoupons />} />
      <Route path="coupons/new" element={<NewCoupon />} />
      <Route path="coupons/:id" element={<NewCoupon />} />
      <Route path="promotions" element={<AllPromotions />} />
      <Route path="promotions/new" element={<NewPromotion />} />
      <Route path="promotions/:id" element={<NewPromotion />} />
      <Route path="sales-returns" element={<AllSalesReturns />} />
      <Route path="sales-returns/new" element={<CreditDebitNoteComposer kind="return" />} />
      <Route path="sales-returns/:id" element={<CreditDebitNoteComposer kind="return" />} />

      {/* Restaurant — tables/menu-categories/menu-items */}
      <Route path="restaurant-tables" element={<AllRestaurantTables />} />
      <Route path="restaurant-tables/new" element={<NewRestaurantTable />} />
      <Route path="restaurant-tables/:id" element={<NewRestaurantTable />} />
      {/* Menu Categories + Items use the registry ApiBrowser /
          GenericFullPageForm — bulk spreadsheet, Excel import/export/
          template, row checkboxes, working edit, and the form already
          renders the category FK dropdown, image upload, and GST
          tax-slab dropdown. (Old hand-built CrudList pages retired.) */}
      <Route path="menu-categories" element={<ModuleRedirect slug="menu-categories" />} />
      <Route path="menu-categories/new" element={<Navigate to="/panel/api/restaurant/MenuCategory/new" replace />} />
      <Route path="menu-categories/:id" element={<MenuRegistryEdit resource="MenuCategory" />} />
      <Route path="menu-items" element={<ModuleRedirect slug="menu-items" />} />
      <Route path="menu-items/new" element={<Navigate to="/panel/api/restaurant/MenuItem/new" replace />} />
      <Route path="menu-items/:id" element={<MenuRegistryEdit resource="MenuItem" />} />

      {/* Single-segment slugs. Each one falls through to ModuleRedirect, */}
      {/* which redirects to /panel/api/<group>/<resource> for the matching */}
      {/* registry entity. Mirrors apps/panel/src/App.tsx.                  */}
      <Route path="warehouses"          element={<ModuleRedirect slug="warehouses" />} />
      <Route path="leave-types"         element={<ModuleRedirect slug="leave-types" />} />
      <Route path="holidays"            element={<ModuleRedirect slug="holidays" />} />
      <Route path="attendance"          element={<ModuleRedirect slug="attendance" />} />
      <Route path="salary-components"   element={<ModuleRedirect slug="salary-components" />} />
      <Route path="salary-structures"   element={<ModuleRedirect slug="salary-structures" />} />
      <Route path="payslips"            element={<ModuleRedirect slug="payslips" />} />
      <Route path="candidates"          element={<ModuleRedirect slug="candidates" />} />
      <Route path="interviews"          element={<ModuleRedirect slug="interviews" />} />
      <Route path="offer-letters"       element={<ModuleRedirect slug="offer-letters" />} />
      <Route path="goals"               element={<ModuleRedirect slug="goals" />} />
      <Route path="kpis"                element={<ModuleRedirect slug="kpis" />} />
      <Route path="stock"               element={<ModuleRedirect slug="stock" />} />
      <Route path="stock-movements"     element={<ModuleRedirect slug="stock-movements" />} />
      <Route path="stock-adjustments"   element={<ModuleRedirect slug="stock-adjustments" />} />
      <Route path="stock-transfers"     element={<ModuleRedirect slug="stock-transfers" />} />
      {/* sales-orders / purchase-orders / credit-notes / etc. have their */}
      {/* /new + /:id routes wired above with TransactionEntryShell. The  */}
      {/* bare paths below redirect to the registry-driven list page.    */}
      <Route path="sales-orders"        element={<ModuleRedirect slug="sales-orders" />} />
      <Route path="invoices"            element={<ModuleRedirect slug="invoices" />} />
      <Route path="payments"            element={<ModuleRedirect slug="payments" />} />
      <Route path="purchase-orders"     element={<ModuleRedirect slug="purchase-orders" />} />
      <Route path="credit-notes"        element={<ModuleRedirect slug="credit-notes" />} />
      <Route path="debit-notes"         element={<ModuleRedirect slug="debit-notes" />} />
      <Route path="delivery-challans"   element={<ModuleRedirect slug="delivery-challans" />} />
      <Route path="sales-invoices"      element={<ModuleRedirect slug="sales-invoices" />} />
      <Route path="purchase-invoices"   element={<ModuleRedirect slug="purchase-invoices" />} />
      <Route path="accounts"            element={<ModuleRedirect slug="accounts" />} />
      <Route path="journal-entries"     element={<ModuleRedirect slug="journal-entries" />} />
      <Route path="ledger-entries"      element={<ModuleRedirect slug="ledger-entries" />} />
      <Route path="bank-transactions"   element={<ModuleRedirect slug="bank-transactions" />} />
      <Route path="taxes"               element={<ModuleRedirect slug="taxes" />} />
      <Route path="reports"             element={<ModuleRedirect slug="reports" />} />
      <Route path="license"             element={<ModuleRedirect slug="license" />} />
      <Route path="settings"            element={<ModuleRedirect slug="settings" />} />

      {/* Catch-all — same as the panel: try ModuleRedirect on the URL.  */}
      <Route path="*" element={<ModuleRedirect />} />
    </>
  );
}
